import { ReactComponent as SearchIcon } from '../../../../../../assets/vectors/search.svg';
import Button from '../../../../../designSystem/Button/Button';
import { DashboardProspectionsTableProps } from './DashboardProspectionsTable.types';
import Select from '../../../../../commons/Select/Select';
import {
  Prospect,
  ProspectStatus,
} from '../../../../../../types/api/endpoints/prospect/prospect.types';
import { useMutation, useQueryClient } from 'react-query';
import {
  SEARCH_PROSPECT_STATUS_QUERY_KEY,
  searchProspectQuery,
} from './DashboardProspectionsTable.query';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { getProspectLabelByStep } from '../../../../../../utils/helpers/prospects/getProspectLabelByStep';
import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import { useMergedStatesFromFetchers } from '../../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import Loading from '../../../../../commons/Loading/Loading';
import Pagination from '../../../../../commons/Pagination/Pagination';
import {
  SearchProspectsItem,
  SearchProspectsResponse,
} from '../../../../../../types/api/endpoints/prospect/directory.types';
import { useProspects } from '../../../../../../hooks/fetchers/useProspects/useProspects';
import { ButtonSize } from '../../../../../designSystem/Button/Button.types';
import SendEmailProspectButton from './SendEmailProspectButton/SendEmailProspectButton';
import UpdateStatusProspectModal from './UpdateStatusProspectModal/UpdateStatusProspectModal';
import moment from 'moment';
import StatusHistoryModal from './StatusHistoryModal/StatusHistoryModal';
import CreateAccountManuallyButton from './CreateAccountManuallyButton/CreateAccountManuallyButton';
import { UseProspectsFilters } from '../../../../../../hooks/fetchers/useProspects/useProspects.types';
import ValidatePaymentModal from './ValidatePaymentModal/ValidatePaymentModal';
import { useLocation } from 'react-router-dom';

export default ({ openModal }: DashboardProspectionsTableProps) => {
  const queryClient = useQueryClient();

  const location = useLocation();

  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
  const [isHistoryModalOpen, setHistoryModalOpen] = useState(false);
  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  const [currentProspect, setCurrentProspect] = useState<Prospect>();

  const [selectedFilter, setSelectedFilter] = useState<UseProspectsFilters>(UseProspectsFilters.ShowAll);

  const [searchResults, setSearchResults] = useState<SearchProspectsItem[]>();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(25);

  const { data: prospects, isLoading: isProspectionsLoading, refetch: refetchProspects } = useProspects({
    page: currentPage,
    perPage: itemsPerPage,
    filter: selectedFilter !== UseProspectsFilters.ShowAll ? selectedFilter : undefined
  });

  const { mutate: searchMutate, isLoading: isSearchLoading } = useMutation<
    Awaited<SearchProspectsResponse>,
    Error,
    string
  >((data) => searchProspectQuery(data), {
    mutationKey: SEARCH_PROSPECT_STATUS_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: (response) => {
      setSearchResults(response);
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  useEffect(() => {
    setCurrentPage(1);
    queryClient.cancelMutations();
    refetchProspects();
  }, [selectedFilter]);

  useEffect(() => {
    if (location.hash === '#relancer') {
      setSelectedFilter(UseProspectsFilters.Relancer)
      location.hash = ''
    }
  }, [])

  const onChangeSearchText = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // if (value.length > 2) {
      //   searchMutate(value)
      // } else if (value.length === 0) {
      //   setSearchResults(undefined)
      // }
      if (value.length === 0) {
        setSearchResults(undefined);
      } else {
        searchMutate(value);
      }
    },
    [searchMutate]
  );

  const numberOfPages = useMemo(() => {
    if (searchResults) {
      // @ts-ignore
      return parseInt(searchResults.total / itemsPerPage);
    } else {
      // @ts-ignore
      return parseInt(prospects?.total / itemsPerPage);
    }
  }, [prospects, searchResults, itemsPerPage]);

  const { isLoading } = useMergedStatesFromFetchers({
    loadings: [isProspectionsLoading, isSearchLoading],
  });

  if (isProspectionsLoading) return <Loading />

  return (
    <div className="dashboard-commerciaux">
      <div className='flex-column'>
        <div className="dashboard-commerciaux__header">
          <SearchInput
            placeholder="Chercher une entreprise..."
            startIcon={SearchIcon}
            onChange={onChangeSearchText}
          />
          {/* <Button onClick={openModal}>Ajouter</Button> */}
        </div>
        {!searchResults &&
          <div className="dashboard__results-header">
          {Object.values(UseProspectsFilters).map((filter, index) => (
            <div
              key={index}
              className={`dashboard__results-header-item ${
                selectedFilter === filter ? 'dashboard__results-header-item--active' : ''
              }`}
              onClick={() => setSelectedFilter(filter)}
            >
              <span>{filter}</span>
            </div>
          ))}
        </div>
        }
      </div>
      {isSearchLoading ? (
        <Loading />
      ) : (
        <>
          {searchResults &&
            (searchResults.length > 0 ? (
              <table className="dashboard__table dashboard-commerciaux__table">
                <thead>
                  <tr>
                    <th>Entreprise</th>
                    <th>Adresse email</th>
                    <th>N° de téléphone</th>
                    <th>Siret</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults?.map(({ name, email, phone, siret, id_step, status }, index) => {
                    const stepLabel = getProspectLabelByStep(id_step);
                    return (
                      <tr key={index}>
                        <td>{name}</td>
                        <td>{email}</td>
                        <td>{phone}</td>
                        <td>{siret}</td>
                        <td>{stepLabel || status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ flex: 1, padding: 'var(--spacing-sToM)' }}>
                <Button
                  onClick={openModal}
                  size={ButtonSize.Large}
                  style={{
                    alignSelf: 'unset',
                    textAlign: 'center',
                    borderRadius: 6,
                    marginBottom: 'var(--spacing-s)',
                    width: '100%',
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}>
                  Ajouter un prospect
                </Button>
              </div>
            ))}
          {!searchResults && prospects && prospects?.results.length > 0 && (
            <table className="dashboard__table dashboard-commerciaux__table dashboard-invoices__table">
              <thead>
                <tr>
                  <th>Entreprise</th>
                  <th>Contact</th>
                  <th>Adresse email</th>
                  <th>N° de téléphone</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {prospects?.results.map((prospect, index) => {
                  const { id, name_company, name, email, phone, status } = prospect;
                  return (
                    <tr
                      key={index}
                      className='dashboard-commerciaux__table-tr--clickable'
                      onClick={(e) => {
                        e.stopPropagation()
                        setCurrentProspect(prospect)
                        setHistoryModalOpen(true)
                      }}
                    >
                      <td>{name_company}</td>
                      <td>{name}</td>
                      <td style={{ textTransform: 'lowercase' }}>{email}</td>
                      <td>{phone}</td>
                      <td className="flex-column" onClick={e => e.stopPropagation()}>
                        <Select
                          options={Object.values(ProspectStatus)
                            .filter((status) => status !== ProspectStatus.Gagné)
                            .map((status) => ({
                              value: status,
                              label: status,
                            }))}
                          // @ts-ignore
                          defaultValue={
                            prospect.status === ProspectStatus.Relancer
                              ? {
                                label: `à relancer le ${moment(prospect.reminder_date).format(
                                  'DD/MM/YYYY'
                                )}`,
                                value: status,
                              }
                              : { label: status, value: status }
                          }
                          // @ts-ignore
                          onChange={({ value }: SelectOption) => {
                            setCurrentProspect({ ...prospect, status: value });
                            setUpdateModalOpen(true);
                            // mutate({ status: value, id, email });
                          }}
                          disabled={status === ProspectStatus.Gagné}
                        />
                        {status === ProspectStatus.AdhésionEnCours && (
                          <div className='flex-column'>
                            <SendEmailProspectButton prospectId={id}>
                              Renvoyer un email d'inscription
                            </SendEmailProspectButton>
                            <Button
                              style={{
                                alignSelf: 'center',
                                marginTop: 'var(--spacing-s)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setCurrentProspect(prospect)
                                setPaymentModalOpen(true)
                              }}>
                              Valider le paiement
                            </Button>
                          </div>
                        )}
                        {status === ProspectStatus.AContacter && (
                          <CreateAccountManuallyButton prospectId={id}>
                            Créer un compte
                          </CreateAccountManuallyButton>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
      {!isLoading && numberOfPages > 0 && (
        <Pagination {...{ currentPage, setCurrentPage, numberOfPages }} />
      )}
      <UpdateStatusProspectModal
        prospect={currentProspect}
        isOpen={isUpdateModalOpen}
        setOpen={setUpdateModalOpen}
        onClose={() => setCurrentProspect(undefined)}
      />
      <StatusHistoryModal
        prospect={currentProspect}
        isOpen={isHistoryModalOpen}
        setOpen={setHistoryModalOpen}
        onClose={() => setCurrentProspect(undefined)}
      />
      <ValidatePaymentModal
        prospect={currentProspect}
        isOpen={isPaymentModalOpen}
        setOpen={setPaymentModalOpen}
        onClose={() => setCurrentProspect(undefined)}
      />
    </div>
  );
};
